import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";

import { Spacings } from "@/styling/spacings";
import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import PermissionService, {
  RequestState,
  RequestType,
} from "@/services/user/permission";

interface RequestPermissionCardProps {
  session: AppUserSession;
  profile: UserProfile;
  bgColors: string[];
  numColumns: number;
  blurIntensity?: number;
}

function getRandomColor(): string {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

function RequestPermissionCard({
  session,
  profile,
  bgColors,
  numColumns,
}: RequestPermissionCardProps): React.JSX.Element {
  const permsService = new PermissionService(session);

  const { width } = useWindowDimensions();
  const itemWidth = (width - Spacings.s1 * (numColumns + 1)) / numColumns;
  const itemHeight = (itemWidth / 3) * 4;
  const imageActionBarHeight = 27;

  const [colors, setColors] = useState([
    getRandomColor(),
    getRandomColor(),
  ] as const);

  const isRequestPending =
    profile.privatePhotoAccess.status === RequestState.INIT;
  const [requestPending, setRequestPending] =
    useState<boolean>(isRequestPending);

  useEffect(() => {
    if (bgColors.length === 0) {
      setColors([getRandomColor(), getRandomColor()]);
    }
  }, []);

  const handlePress = (): void => {
    permsService.createRequest(RequestType.PRIVATE_PHOTOS, profile.username);
    setRequestPending(true);
  };

  return (
    <View>
      <TouchableOpacity onPress={handlePress} disabled={requestPending}>
        <LinearGradient
          colors={colors}
          style={[
            styles.container,
            { height: itemHeight + imageActionBarHeight, width: itemWidth },
          ]}
        >
          {requestPending ? (
            <MaterialCommunityIcons name="timer-sand" size={48} color="white" />
          ) : (
            <MaterialIcons name="lock" size={48} color="white" />
          )}
          <Text style={styles.text}>
            {requestPending ? "Pending Request" : "Request Access"}
          </Text>
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    borderRadius: 10,
    flex: 1,
    justifyContent: "center",
    overflow: "hidden",
  },
  text: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 10,
  },
});

export default RequestPermissionCard;
